import React from 'react';
import classNames from 'classnames';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useManagedAccountPageEventLogger
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import { Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import Divider from 'Components/UI/Divider';

const useStyles = buildUseStyles('ManagedAccountActivityInfo', (theme) => ({
  metaFormInline: {
    padding: 0,
    backgroundColor: 'inherit',
  },

  infoContainer: {
    width: '100%',
  },
  info: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const tooltipDelay = 500;

function InfoSimple({
  label, value, hidden, test, resource, attrName,
}) {
  const classes = useStyles();
  const { logActivitySummaryInfo } = useManagedAccountPageEventLogger();

  function handleLabelClick() {
    logActivitySummaryInfo(`${label} label`);
  }

  function handleValueClick() {
    logActivitySummaryInfo(`${label} value`);
  }

  if (hidden) return null;
  if (!value) return null;

  const description = resource.meta().field(attrName).attr('description');
  return (<>
    <Box className={classNames(classes.infoContainer, classes.info)} test={test}>
      <Tooltip title={description} disableHoverListener={!description} enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
        <Typography variant="h5" weight="light" test={`${test}--label`} onClick={handleLabelClick}>{label}</Typography>
      </Tooltip>
      <Typography variant="h5" test={`${test}--value`} onClick={handleValueClick}>{value}</Typography>
    </Box>
  </>);
}

function InfoHeader({
  label, value, hidden, test, resource, attrName,
}) {
  const classes = useStyles();
  const { logActivitySummaryInfo } = useManagedAccountPageEventLogger();

  function handleLabelClick() {
    logActivitySummaryInfo(`${label} label`);
  }

  function handleValueClick() {
    logActivitySummaryInfo(`${label} value`);
  }

  if (hidden) return null;

  const description = resource.meta().field(attrName).attr('description');

  return (<>
    <Box className={classes.infoContainer} test={test}>
      <Box className={classes.info}>
        <Tooltip title={description} disableHoverListener={!description} enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
          <Typography color="primary" variant="h4" test={`${test}--label`} onClick={handleLabelClick}>{label}</Typography>
        </Tooltip>
        <Typography color="primary" variant="h4" test={`${test}--value`} onClick={handleValueClick}>{value}</Typography>
      </Box>
      <Divider />
    </Box>
  </>);
}

function InfoFooter({
  label, value, hidden, test, resource, attrName,
}) {
  const classes = useStyles();
  const { logActivitySummaryInfo } = useManagedAccountPageEventLogger();

  function handleLabelClick() {
    logActivitySummaryInfo(`${label} label`);
  }

  function handleValueClick() {
    logActivitySummaryInfo(`${label} value`);
  }

  if (hidden) return null;
  const description = resource.meta().field(attrName).attr('description');

  return (<>
    <Box className={classes.infoContainer} test={test}>
      <Divider />
      <Box className={classes.info}>
        <Tooltip title={description} disableHoverListener={!description} enterDelay={tooltipDelay} enterNextDelay={tooltipDelay}>
          <Typography color="naturalGreen" variant="h4" test={`${test}--label`} onClick={handleLabelClick}>{label}</Typography>
        </Tooltip>
        <Typography color="naturalGreen" variant="h4" test={`${test}--value`} onClick={handleValueClick}>{value}</Typography>
      </Box>
    </Box>
  </>);
}

const metaOverrideSet = fr.metaOverride()
  .overrideAll({
    _component: () => InfoSimple,
    _spreadPropNames: ['resource', 'attrName'],
  }, { ordinal: 200 })
  .nameOverride('startingBalance', {
    _component: () => InfoHeader,
    _spreadPropNames: ['resource', 'attrName'],
  }, { ordinal: 201 })
  .nameOverride('endingBalance', {
    _component: () => InfoFooter,
    _spreadPropNames: ['resource', 'attrName'],
  }, { ordinal: 201 })
  // This attribute should only show in one special place and in mobile only
  .nameOverride('accruedInterestAllTime', {
    hidden: true,
  }, { ordinal: 201 });

export default function ManagedAccountActivityInfo({ activitySummary }) {
  const classes = useStyles();

  if (!activitySummary) return null;

  return (
    <MetaFormInline
      className={classes.metaFormInline}
      resource={activitySummary}
      metaOverrideSet={metaOverrideSet}
      noIndividualSubmitButtons
    />
  );
}
ManagedAccountActivityInfo.defaultProps = {
  activitySummary: undefined,
};
ManagedAccountActivityInfo.propTypes = {
  activitySummary: PropTypes.instanceOf(fr.Resource),
};
